<script lang="ts">
  import { Check, LoaderCircle } from '@lucide/svelte';
  import { onDestroy, type Snippet, untrack } from 'svelte';
  import type { HTMLButtonAttributes } from 'svelte/elements';
  import { cn } from '../lib/cn';
  import { type ButtonSize, type ButtonVariant, buttonVariants } from './button';

  interface Props extends HTMLButtonAttributes {
    variant?: ButtonVariant;
    size?: ButtonSize;
    loading?: boolean;
    /**
     * The work this button started was confirmed: pass the mutation's `isSuccess`, nothing earlier.
     * A small tick shows when it turns true; a failure stays beside the form, as it always did.
     */
    done?: boolean;
    children?: Snippet;
  }

  let { class: className, variant, size, loading, done, disabled, children, type = 'button', ...rest }: Props = $props();

  const TICK_MS = 900;
  let ticking = $state(false);
  // Only the moment it turns true: a mutation stays successful long after, and a button may mount then.
  let wasDone = untrack(() => !!done);
  let timer: ReturnType<typeof setTimeout> | undefined;
  $effect(() => {
    const now = !!done;
    if (now && !wasDone) {
      ticking = true;
      clearTimeout(timer);
      timer = setTimeout(() => (ticking = false), TICK_MS);
    }
    wasDone = now;
  });
  onDestroy(() => clearTimeout(timer));
  const showing = $derived(loading ? 'loading' : ticking ? 'done' : null);
</script>

<!-- The label stays in place, only unseen, so the width never moves and the name stays for screen readers. -->
<button
  {type}
  class={cn(buttonVariants({ variant, size }), className as string | undefined, showing && 'text-transparent opacity-100!')}
  disabled={disabled || loading}
  aria-busy={loading || undefined}
  {...rest}
>
  {@render children?.()}
  {#if showing === 'loading'}
    <span class="btn-status"><LoaderCircle class="size-4 animate-spin" aria-hidden="true" /></span>
  {:else if showing === 'done'}
    <span class="btn-status"><Check class="tick-in size-4" aria-hidden="true" /></span>
  {/if}
</button>
